/**
 * dtcg-css-compiler.ts
 *
 * Ahead-of-Time (AOT) Edge CSS Compiler & Density Scaler (Sprint THEME-02):
 * - Compiles W3C DTCG tokens to minified atomic CSS custom properties.
 * - Sanitizes hex colors, pixel radii, and density variables.
 * - Outputs standalone :root { ... } strings with zero runtime dependencies.
 */

import type { ThemeDefinition, TenantThemeOverride, DensityMode } from '../theme-contracts.js';

export class DtcgCssCompiler {
  public static sanitizeHex(hex?: string): string | null {
    if (!hex) return null;
    const clean = hex.trim();
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean) ? clean : null;
  }

  public static sanitizeRadius(radiusPx?: number): string | null {
    if (radiusPx === undefined || radiusPx === null) return null;
    const bounded = Math.min(32, Math.max(0, radiusPx));
    return `${bounded}px`;
  }

  public static getDensityVariables(density: DensityMode = 'comfortable'): Record<string, string> {
    switch (density) {
      case 'compact':
        return {
          '--spacing-unit': '3px',
          '--control-height': '32px',
          '--table-padding-y': '6px',
          '--font-size-base': '13px',
        };
      case 'high_density':
        return {
          '--spacing-unit': '2px',
          '--control-height': '26px',
          '--table-padding-y': '4px',
          '--font-size-base': '12px',
        };
      case 'comfortable':
      default:
        return {
          '--spacing-unit': '4px',
          '--control-height': '40px',
          '--table-padding-y': '10px',
          '--font-size-base': '14px',
        };
    }
  }

  public static compileToCssVariables(
    baseTheme: ThemeDefinition,
    overrides?: TenantThemeOverride
  ): Record<string, string> {
    const brandPrimary = this.sanitizeHex(overrides?.brandPrimaryHex) ?? baseTheme.colors.brandPrimary.$value;
    const brandOnPrimary = this.sanitizeHex(overrides?.brandOnPrimaryHex) ?? baseTheme.colors.brandOnPrimary.$value;
    const surfaceCanvas = this.sanitizeHex(overrides?.surfaceCanvasHex) ?? baseTheme.colors.surfaceCanvas.$value;
    const surfacePanel = this.sanitizeHex(overrides?.surfacePanelHex) ?? baseTheme.colors.surfacePanel.$value;

    const radiusCard = this.sanitizeRadius(overrides?.radiusCardPx) ?? baseTheme.geometry.radiusCard.$value;
    const radiusControl = this.sanitizeRadius(overrides?.radiusControlPx) ?? baseTheme.geometry.radiusControl.$value;

    const densityVars = this.getDensityVariables(overrides?.density ?? 'comfortable');

    return {
      '--surface-canvas': surfaceCanvas,
      '--surface-panel': surfacePanel,
      '--surface-panel-subtle': baseTheme.colors.surfacePanelSubtle.$value,
      '--border-subtle': baseTheme.colors.borderSubtle.$value,
      '--border-strong': baseTheme.colors.borderStrong.$value,
      '--text-primary': baseTheme.colors.textPrimary.$value,
      '--text-secondary': baseTheme.colors.textSecondary.$value,
      '--text-muted': baseTheme.colors.textMuted.$value,
      '--brand-primary': brandPrimary,
      '--brand-primary-hover': baseTheme.colors.brandPrimaryHover.$value,
      '--brand-on-primary': brandOnPrimary,
      '--accent-success': baseTheme.colors.accentSuccess.$value,
      '--accent-warning': baseTheme.colors.accentWarning.$value,
      '--accent-danger': baseTheme.colors.accentDanger.$value,
      '--focus-ring': baseTheme.colors.focusRing.$value,
      '--radius-card': radiusCard,
      '--radius-control': radiusControl,
      '--font-sans': baseTheme.geometry.fontSans.$value,
      '--font-mono': baseTheme.geometry.fontMono.$value,
      // ─── Theme Aliases ──────────────────────────────────────────────
      '--theme-bg-app': surfaceCanvas,
      '--theme-bg-card': surfacePanel,
      '--theme-bg-surface': surfacePanel,
      '--theme-border': baseTheme.colors.borderSubtle.$value,
      '--theme-border-strong': baseTheme.colors.borderStrong.$value,
      '--theme-text-primary': baseTheme.colors.textPrimary.$value,
      '--theme-text-secondary': baseTheme.colors.textSecondary.$value,
      '--theme-text-muted': baseTheme.colors.textMuted.$value,
      '--theme-accent': brandPrimary,
      '--theme-primary': brandPrimary,
      '--theme-radius-md': radiusControl,
      '--theme-radius-lg': radiusCard,
      // ─── Mesh Design Tokens ────────────────────────────────────────
      '--mesh-color-bg': surfaceCanvas,
      '--mesh-color-surface': surfacePanel,
      '--mesh-color-surface-elevated': baseTheme.colors.surfacePanelSubtle.$value,
      '--mesh-color-primary': brandPrimary,
      '--mesh-color-primary-hover': baseTheme.colors.brandPrimaryHover.$value,
      '--mesh-color-text': baseTheme.colors.textPrimary.$value,
      '--mesh-color-text-muted': baseTheme.colors.textMuted.$value,
      '--mesh-color-border': baseTheme.colors.borderStrong.$value,
      '--mesh-color-border-subtle': baseTheme.colors.borderSubtle.$value,
      '--mesh-color-error': baseTheme.colors.accentDanger.$value,
      '--mesh-color-warning': baseTheme.colors.accentWarning.$value,
      '--mesh-color-success': baseTheme.colors.accentSuccess.$value,
      '--mesh-radius-sm': '4px',
      '--mesh-radius-md': radiusControl,
      '--mesh-radius-lg': radiusCard,
      '--mesh-font-sans': baseTheme.geometry.fontSans.$value,
      '--mesh-font-mono': baseTheme.geometry.fontMono.$value,
      ...densityVars,
    };

  }

  public static compileToAotCss(
    baseTheme: ThemeDefinition,
    overrides?: TenantThemeOverride,
    selector = ':root'
  ): string {
    const cssVars = this.compileToCssVariables(baseTheme, overrides);
    const body = Object.entries(cssVars)
      .map(([prop, val]) => `${prop}:${val}`)
      .join(';');

    return `${selector}{${body}}`;
  }
}
